import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Search } from "lucide-react";

export default function AgentEditor() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    name: "", tone: "professional", personality: "",
    system_prompt: "", business_context: "", knowledge_base_ids: [], status: "active"
  });
  const [kbEntries, setKbEntries] = useState([]);
  const [kbSearch, setKbSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const filteredKb = kbEntries.filter((e) =>
    !kbSearch.trim() || (e.title || "").toLowerCase().includes(kbSearch.trim().toLowerCase())
  );
  const [testMsg, setTestMsg] = useState("");
  const [testReply, setTestReply] = useState("");
  const [testConvoId, setTestConvoId] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: kb } = await api.get("/knowledge-base");
        setKbEntries(kb);
        if (isEdit) {
          const { data: agent } = await api.get(`/agents/${id}`);
          setForm({
            name: agent.name || "",
            tone: agent.tone || "professional",
            personality: agent.personality || "",
            system_prompt: agent.system_prompt || "",
            business_context: agent.business_context || "",
            knowledge_base_ids: agent.knowledge_base_ids || [],
            status: agent.status || "active",
          });
        }
      } catch (e) {
        toast.error(t("agentEditor.toastLoadFailed"));
      }
    };
    load();
  }, [id, isEdit]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t("agentEditor.toastNameRequired")); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/agents/${id}`, form);
        toast.success(t("agentEditor.toastUpdated"));
      } else {
        await api.post("/agents", form);
        toast.success(t("agentEditor.toastCreated"));
      }
      navigate("/agents");
    } catch (e) {
      toast.error(t("agentEditor.toastSaveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testMsg.trim()) return;
    if (!isEdit) { toast.error(t("agentEditor.toastSaveFirst")); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/chat/test", {
        agent_id: id, message: testMsg, conversation_id: testConvoId
      });
      setTestReply(data.response);
      setTestConvoId(data.conversation_id);
      setTestMsg("");
    } catch (e) {
      toast.error(t("agentEditor.toastTestFailed"));
    } finally {
      setTesting(false);
    }
  };

  const toggleKB = (kbId) => {
    setForm(prev => {
      const ids = prev.knowledge_base_ids.includes(kbId)
        ? prev.knowledge_base_ids.filter(x => x !== kbId)
        : [...prev.knowledge_base_ids, kbId];
      return { ...prev, knowledge_base_ids: ids };
    });
  };

  return (
    <div data-testid="agent-editor-page" className="p-8 animate-fade-in max-w-4xl">
      <div className="flex items-center gap-4 mb-8">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/agents")}
          className="p-2 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-['Manrope'] font-extrabold text-foreground">
            {isEdit ? t("agentEditor.editAgent") : t("agentEditor.newAgent")}
          </h1>
        </div>
      </div>

      <Tabs defaultValue="persona" className="space-y-6">
        <TabsList className="bg-secondary border border-border">
          <TabsTrigger value="persona" data-testid="tab-persona">{t("agentEditor.tabPersona")}</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="tab-knowledge">{t("agentEditor.tabKnowledge")}</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test">{t("agentEditor.tabTest")}</TabsTrigger>
        </TabsList>

        <TabsContent value="persona">
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">{t("agentEditor.personaTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("agentEditor.agentName")}</Label>
                <Input
                  data-testid="agent-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("agentEditor.agentNamePlaceholder")}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("agentEditor.tone")}</Label>
                <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                  <SelectTrigger data-testid="agent-tone-select" className="bg-secondary/50 border-transparent focus:border-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="professional">{t("agentEditor.toneProfessional")}</SelectItem>
                    <SelectItem value="friendly">{t("agentEditor.toneFriendly")}</SelectItem>
                    <SelectItem value="casual">{t("agentEditor.toneCasual")}</SelectItem>
                    <SelectItem value="formal">{t("agentEditor.toneFormal")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("agentEditor.personality")}</Label>
                <Input
                  data-testid="agent-personality-input"
                  value={form.personality}
                  onChange={(e) => setForm({ ...form, personality: e.target.value })}
                  placeholder={t("agentEditor.personalityPlaceholder")}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("agentEditor.systemPrompt")}</Label>
                <Textarea
                  data-testid="agent-system-prompt"
                  value={form.system_prompt}
                  onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
                  placeholder={t("agentEditor.systemPromptPlaceholder")}
                  rows={5}
                  className="bg-secondary/50 border-transparent focus:border-primary resize-none"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">{t("agentEditor.businessContext")}</Label>
                <Textarea
                  data-testid="agent-business-context"
                  value={form.business_context}
                  onChange={(e) => setForm({ ...form, business_context: e.target.value })}
                  placeholder={t("agentEditor.businessContextPlaceholder")}
                  rows={4}
                  className="bg-secondary/50 border-transparent focus:border-primary resize-none"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="knowledge">
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">{t("agentEditor.kbTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {t("agentEditor.kbHint")}
              </p>
              {kbEntries.length === 0 ? (
                <p className="text-muted-foreground/60 text-sm">{t("agentEditor.kbEmpty")}</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        data-testid="kb-search"
                        value={kbSearch}
                        onChange={(e) => setKbSearch(e.target.value)}
                        placeholder={t("agentEditor.kbSearch")}
                        className="pl-9 bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="kb-toggle-all"
                      onClick={() => {
                        const filteredIds = filteredKb.map(e => e.id);
                        const allSelected = filteredIds.every(id => form.knowledge_base_ids.includes(id));
                        setForm(prev => ({
                          ...prev,
                          knowledge_base_ids: allSelected
                            ? prev.knowledge_base_ids.filter(id => !filteredIds.includes(id))
                            : [...new Set([...prev.knowledge_base_ids, ...filteredIds])],
                        }));
                      }}
                    >
                      {filteredKb.length > 0 && filteredKb.every(e => form.knowledge_base_ids.includes(e.id)) ? t("agentEditor.deselectAll") : t("agentEditor.selectAll")}
                    </Button>
                  </div>
                  {filteredKb.length === 0 && (
                    <p className="text-muted-foreground/60 text-sm">{t("agentEditor.noResults", { q: kbSearch })}</p>
                  )}
                  {filteredKb.map((entry) => (
                    <label
                      key={entry.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary cursor-pointer transition-colors duration-200"
                    >
                      <Checkbox
                        data-testid={`kb-check-${entry.id}`}
                        checked={form.knowledge_base_ids.includes(entry.id)}
                        onCheckedChange={() => toggleKB(entry.id)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{entry.title}</p>
                        <p className="text-xs text-muted-foreground">v{entry.version}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test">
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">{t("agentEditor.testTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              {!isEdit ? (
                <p className="text-sm text-muted-foreground">{t("agentEditor.saveFirst")}</p>
              ) : (
                <div className="space-y-4">
                  {testReply && (
                    <div className="p-4 rounded-lg bg-secondary/50 text-sm text-foreground" data-testid="test-reply">
                      <p className="text-xs text-muted-foreground mb-1">{t("agentEditor.agentResponse")}</p>
                      {testReply}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      data-testid="test-message-input"
                      value={testMsg}
                      onChange={(e) => setTestMsg(e.target.value)}
                      placeholder={t("agentEditor.testPlaceholder")}
                      className="bg-secondary/50 border-transparent focus:border-primary"
                      onKeyDown={(e) => e.key === "Enter" && handleTest()}
                    />
                    <Button
                      data-testid="send-test-btn"
                      onClick={handleTest}
                      disabled={testing || !testMsg.trim()}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-transform duration-150"
                    >
                      {testing ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end mt-6 gap-3">
        <Button
          variant="outline"
          onClick={() => navigate("/agents")}
          className="border-border text-foreground hover:bg-white/5"
        >
          {t("agentEditor.cancel")}
        </Button>
        <Button
          data-testid="save-agent-btn"
          onClick={handleSave}
          disabled={loading}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,230,118,0.3)] active:scale-95 transition-transform duration-150"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? t("agentEditor.saving") : t("agentEditor.saveAgent")}
        </Button>
      </div>
    </div>
  );
}
